import { t } from "i18next";
import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  Legend,
} from "recharts";

/**
 * Configuration interface for individual bar series
 * Defines properties for each bar including key, color, and optional customizations
 */
interface BarKeyConfig {
  key: string; // Data key for the bar series
  color: string; // Fill color for the bar
  label?: string; // Display label (defaults to key if not provided)
  barProps?: Record<string, any>; // Additional props to pass to the Bar component
}

/**
 * Props interface for LivisBarVerticalChart component
 * Defines all configurable options for the vertical bar chart
 */
interface LivisBarVerticalChartProps {
  data: any[]; // Chart data array
  barKeys: BarKeyConfig[]; // Configuration for each bar series
  xAxisDataKey?: string; // Key for X-axis data (usually categories)
  yAxisLabel?: string; // Label for Y-axis
  noDataMessage?: string; // Message to display when no data is available
  height?: number; // Chart height in pixels
  legendPosition?: "top" | "bottom" | "left" | "right"; // Legend placement
  legendAlign?: "left" | "center" | "right"; // Legend alignment within its position
  customTooltip?: (props: any) => React.ReactNode; // Custom tooltip component
  customLegend?: (props: any) => React.ReactNode; // Custom legend component
  customLabel?: (props: any) => React.ReactNode; // Custom label component for bars
  margin?: { top?: number; right?: number; left?: number; bottom?: number }; // Chart margins
}

/**
 * Default label component for bars
 * Renders rotated text inside each bar showing the value
 */
const DefaultLabel = (props: any) => {
  const { x, y, width, height, value } = props;
  return (
    <text
      x={x + width / 2} // Center horizontally
      y={y + height / 2} // Center vertically
      fill="#fff"
      textAnchor="middle"
      dominantBaseline="middle"
      fontSize="var(--livis-font-size-xs)"
      fontWeight="bold"
      transform={`rotate(90, ${x + width / 2}, ${y + height / 2})`} // Rotate 90 degrees
    >
      {value}
    </text>
  );
};

/**
 * Default tooltip component
 * Displays data values when hovering over chart elements
 */
const DefaultTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div 
     // style={{ background: '#fff', padding: 8, border: '1px solid #ccc', borderRadius: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
      className="livis-vertical-bar-chart-tooltip-wrap"
      >
        <p 
        className="livis-vertical-bar-chart-tooltip-label"
       // style={{ margin: 0, marginBottom: 4, fontWeight: 'bold' }}
        >{label}</p>
        {payload.map((entry: any, idx: number) => (
          <p key={idx} style={{ color: entry.color, margin: 0 }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

/**
 * Default legend component
 * Renders clickable legend items with color indicators
 * Supports hiding/showing individual bar series
 */
const DefaultLegend = (props: any) => {
  const { payload, onClick } = props;
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
      {payload.map((entry: any, idx: number) => (
        <div
          key={idx}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer', 
            opacity: entry.inactive ? 0.5 : 1, // Dim inactive items
            minWidth: 'fit-content'
          }}
          onClick={() => onClick && onClick(entry.dataKey)}
        >
          {/* Color indicator */}
          <div style={{ 
            width: 12, 
            height: 12, 
            background: entry.color, 
            marginRight: 6,
            borderRadius: 2,
            flexShrink: 0
          }} />
          <span style={{ fontSize: 'var(--livis-font-size-xs)', whiteSpace: 'nowrap' }}>{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

/**
 * LivisBarVerticalChart - A customizable vertical bar chart component
 * Built on top of Recharts with support for multiple bar series, interactive legends,
 * and flexible positioning options
 */
const LivisBarVerticalChart: React.FC<LivisBarVerticalChartProps> = ({
  data = [],
  barKeys = [],
  xAxisDataKey = 'name',
  yAxisLabel,
  noDataMessage = `${t("No Record Found")}`,
  height = 400,
  legendPosition = 'top',
  legendAlign = 'right',
  customTooltip,
  customLegend,
  customLabel,
  margin = { top: 20, right: 30, left: 20, bottom: 5 },
}) => {
  // State to track which bar series are hidden (for interactive legend)
  const [hiddenBars, setHiddenBars] = useState<{ [key: string]: boolean }>(
    barKeys.reduce((acc, bar) => ({ ...acc, [bar.key]: false }), {})
  );

  /**
   * Handles legend item clicks to toggle bar visibility
   * @param key - The bar key to toggle
   */
  const handleLegendClick = (key: string) => {
    setHiddenBars((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Render empty state when no data is available
  if (!data || data.length === 0) {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart>
          <text 
            x="50%" 
            y="50%" 
            textAnchor="middle" 
            dominantBaseline="middle"
            fontSize="var(--livis-font-size-sm)"
            fill="#666"
          >
            {noDataMessage}
          </text>
        </BarChart>
      </ResponsiveContainer>
    );
  }

  // Prepare legend payload for recharts Legend component
  const legendPayload = barKeys.map((bar) => ({
    value: bar.label || bar.key, // Use label or fallback to key
    color: bar.color,
    dataKey: bar.key,
    inactive: hiddenBars[bar.key], // Mark as inactive if hidden
  }));

  // Calculate chart margins based on legend position to prevent overlap
  const adjustedMargin = { ...margin };
  if (legendPosition === 'top') {
    adjustedMargin.top = (margin.top || 20) + 40;
  } else if (legendPosition === 'bottom') {
    adjustedMargin.bottom = (margin.bottom || 5) + 40;
  } else if (legendPosition === 'left') {
    adjustedMargin.left = (margin.left || 20) + 100;
  } else if (legendPosition === 'right') {
    adjustedMargin.right = (margin.right || 30) + 100;
  }

  // Configure legend properties based on position and alignment
  const legendProps: any = {
    payload: legendPayload,
    content: customLegend || <DefaultLegend onClick={handleLegendClick} />,
  };

  // Set legend position and alignment properties
  if (legendPosition === "top") {
    legendProps.verticalAlign = "top";
    legendProps.align = legendAlign;
    legendProps.layout = "horizontal";
    legendProps.wrapperStyle = {
      paddingBottom: "20px",
      ...(legendAlign === 'left' && { textAlign: 'left' }),
      ...(legendAlign === 'center' && { textAlign: 'center' }),
      ...(legendAlign === 'right' && { textAlign: 'right' }),
    };
  } else if (legendPosition === "bottom") {
    legendProps.verticalAlign = "bottom";
    legendProps.align = legendAlign;
    legendProps.layout = "horizontal";
    legendProps.wrapperStyle = {
      paddingTop: "20px",
      ...(legendAlign === 'left' && { textAlign: 'left' }),
      ...(legendAlign === 'center' && { textAlign: 'center' }),
      ...(legendAlign === 'right' && { textAlign: 'right' }),
    };
  } else if (legendPosition === "left") {
    legendProps.verticalAlign = "middle";
    legendProps.align = "left";
    legendProps.layout = "vertical";
    legendProps.wrapperStyle = {
      paddingRight: "20px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      height: "100%",
    };
  } else if (legendPosition === "right") {
    legendProps.verticalAlign = "middle";
    legendProps.align = "right";
    legendProps.layout = "vertical";
    legendProps.wrapperStyle = {
      paddingLeft: "20px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      height: "100%",
    };
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={adjustedMargin}>
        {/* Grid lines for better readability */}
        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
        
        {/* X-Axis configuration (categories) */}
        <XAxis 
          dataKey={xAxisDataKey} 
          tick={{ fontSize: 'var(--livis-font-size-xxs)' }}
          axisLine={{ stroke: '#d0d0d0' }}
          tickLine={{ stroke: '#d0d0d0' }}
        />
        
        {/* Y-Axis configuration (values) */}
        <YAxis 
          label={yAxisLabel ? { 
            value: yAxisLabel, 
            angle: -90, 
            position: 'insideLeft',
            style: { textAnchor: 'middle' }
          } : undefined}
          tick={{ fontSize: 'var(--livis-font-size-xxs)' }}
          axisLine={{ stroke: '#d0d0d0' }}
          tickLine={{ stroke: '#d0d0d0' }}
        />
        
        {/* Tooltip for hover interactions */}
        <Tooltip 
          content={customTooltip || <DefaultTooltip />} 
          cursor={{ fill: 'rgba(0,0,0,0.05)' }} // Subtle hover highlight
        />
        
        {/* Legend component */}
        <Legend {...legendProps} />
        
        {/* Render bars for each non-hidden series */}
        {barKeys.map((bar) =>
          !hiddenBars[bar.key] ? (
            <Bar
              key={bar.key}
              dataKey={bar.key}
              fill={bar.color}
              barSize={20} // Fixed bar width
              stackId="a" // Stack all bars together
              {...(bar.barProps || {})} // Spread any additional bar properties
            >
              {/* Add custom labels if provided */}
              {customLabel && <LabelList dataKey={bar.key} content={customLabel} />}
            </Bar>
          ) : null
        )}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default LivisBarVerticalChart;